import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, X, ArrowRight, CornerDownLeft } from 'lucide-react';
import { PRODUCTS } from '../data';
import './SearchModal.css';

const PAGES = [
  { title: 'Home', path: '/', icon: '🏠', desc: 'Insurance agency homepage' },
  { title: 'About Us', path: '/about', icon: '👥', desc: 'Our team and company story' },
  { title: 'All Products', path: '/products', icon: '📋', desc: 'View all insurance products' },
  { title: 'Transportation Insurance', path: '/transportation', icon: '🚛', desc: 'Trucking and fleet coverage' },
  { title: 'Certificate of Insurance', path: '/certificate', icon: '📄', desc: 'Search and view your certificate (FEIN required)' },
  { title: 'Get a Quote', path: '/get-quote', icon: '💬', desc: 'Request your free quote' },
  { title: 'Financing Options', path: '/financing', icon: '💳', desc: 'Payment plans available' },
  { title: 'Careers', path: '/careers', icon: '💼', desc: 'Join our team' },
  { title: 'Contact Us', path: '/contact', icon: '📞', desc: 'Get in touch' },
];

const FILTER_TABS = ['All', 'Pages', 'Products'];
const QUICK_LINKS = [
  { label: 'Trucking Insurance', path: '/products/transportation' },
  { label: 'Get a Quote', path: '/get-quote' },
  { label: 'Certificate Lookup', path: '/certificate' },
  { label: 'Business Insurance', path: '/products/business-owners' },
  { label: 'Workers Comp', path: '/products/workers-compensation' },
];

function highlight(text, query) {
  if (!query) return text;
  const idx = text.toLowerCase().indexOf(query.toLowerCase());
  if (idx === -1) return text;
  return (
    <>
      {text.slice(0, idx)}
      <mark>{text.slice(idx, idx + query.length)}</mark>
      {text.slice(idx + query.length)}
    </>
  );
}

export default function SearchModal({ onClose }) {
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState('All');
  const [active, setActive] = useState(0);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  const q = query.trim().toLowerCase();
  const hasQuery = q.length > 0;

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const pageResults = (filter === 'All' || filter === 'Pages') && hasQuery
    ? PAGES.filter(p => p.title.toLowerCase().includes(q) || p.desc.toLowerCase().includes(q))
    : [];

  const productResults = (filter === 'All' || filter === 'Products') && hasQuery
    ? PRODUCTS.filter(p =>
        p.title.toLowerCase().includes(q) ||
        p.tagline.toLowerCase().includes(q) ||
        p.description.toLowerCase().includes(q) ||
        p.keywords.some(k => k.toLowerCase().includes(q))
      )
    : [];

  // Flattened, ordered list for keyboard navigation
  const items = [
    ...productResults.map(p => ({ type: 'product', data: p })),
    ...pageResults.map(p => ({ type: 'page', data: p })),
  ];
  const totalResults = items.length;

  const counts = {
    Products: productResults.length,
    Pages: pageResults.length,
  };

  useEffect(() => { setActive(0); }, [query, filter]);

  const goTo = (item) => {
    onClose();
    if (item.type === 'product') {
      navigate(`/products/${item.data.slug}`);
    } else {
      navigate(item.data.path);
    }
  };

  const handleKey = (e) => {
    if (e.key === 'Escape') { onClose(); return; }
    if (!totalResults) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive(a => (a + 1) % totalResults); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive(a => (a - 1 + totalResults) % totalResults); }
    else if (e.key === 'Enter') { e.preventDefault(); goTo(items[active]); }
  };

  let idx = -1;
  const rowClass = () => { idx += 1; return { i: idx, className: `search-result-item${idx === active ? ' active' : ''}` }; };

  return (
    <div className="search-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="search-modal">
        <button className="search-close" onClick={onClose}><X size={18} /></button>

        {/* Input */}
        <div className="search-input-wrap">
          <Search size={22} className="search-input-icon" />
          <input
            ref={inputRef}
            type="text"
            className="search-input"
            placeholder="Search products and pages…"
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={handleKey}
          />
        </div>

        {/* Filter Tabs */}
        <div className="search-tabs">
          {FILTER_TABS.map(tab => (
            <button
              key={tab}
              className={`search-tab${filter === tab ? ' active' : ''}`}
              onClick={() => setFilter(tab)}
            >
              {tab}
              {hasQuery && tab !== 'All' && counts[tab] > 0 && (
                <span className="search-tab-count">{counts[tab]}</span>
              )}
            </button>
          ))}
        </div>

        {/* Quick Links (no query) */}
        {!hasQuery && (
          <>
            <div className="search-quick-label">Quick Links</div>
            <div className="search-quick-chips">
              {QUICK_LINKS.map(l => (
                <Link key={l.path} to={l.path} className="search-chip" onClick={onClose}>
                  <ArrowRight size={12} className="search-chip-icon" />
                  {l.label}
                </Link>
              ))}
            </div>
            <div className="search-hint-box">
              <Search size={16} style={{ opacity: 0.6 }} />
              <span>Looking for your Certificate of Insurance? Use the secure Certificate page — your FEIN keeps it private.</span>
            </div>
          </>
        )}

        {/* Results */}
        {hasQuery && (
          <>
            <div className="search-results-label">
              {totalResults} result{totalResults !== 1 ? 's' : ''} for "{query}"
            </div>

            {totalResults === 0 ? (
              <div className="search-no-results">
                <div className="search-no-results-icon">🔍</div>
                <p>No results found. Try a different keyword, or call 818-600-0821.</p>
              </div>
            ) : (
              <div className="search-results">
                {productResults.map(p => {
                  const r = rowClass();
                  return (
                    <Link key={p.slug} to={`/products/${p.slug}`} className={r.className} onMouseEnter={() => setActive(r.i)} onClick={onClose}>
                      <div className="search-result-icon">{p.icon}</div>
                      <div className="search-result-body">
                        <div className="search-result-title">{highlight(p.title, q)}</div>
                        <div className="search-result-sub">{p.tagline}</div>
                      </div>
                      <span className="search-result-tag tag-product">Product</span>
                    </Link>
                  );
                })}
                {pageResults.map(p => {
                  const r = rowClass();
                  return (
                    <Link key={p.path} to={p.path} className={r.className} onMouseEnter={() => setActive(r.i)} onClick={onClose}>
                      <div className="search-result-icon">{p.icon}</div>
                      <div className="search-result-body">
                        <div className="search-result-title">{highlight(p.title, q)}</div>
                        <div className="search-result-sub">{p.desc}</div>
                      </div>
                      <span className="search-result-tag tag-page">Page</span>
                    </Link>
                  );
                })}
              </div>
            )}

            {totalResults > 0 && (
              <div className="search-footer-hint">
                <span><kbd>↑</kbd><kbd>↓</kbd> navigate</span>
                <span><kbd><CornerDownLeft size={11} /></kbd> open</span>
                <span><kbd>esc</kbd> close</span>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}